import { type ReactElement, Suspense } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useWidgetState } from 'components/management/customDashboard/CustomWidgetContext.tsx';
import { useRiskDashboardInputSuspenseQuery } from 'generated/graphql.tsx';
import { updateWidgetState } from 'components/management/customDashboard/CustomWidgetContext.tsx';
import Loader from 'components/technical/Loader/Loader.tsx';
import { calculateLabelToMetricParams, isWarmUpPeriodParamEnabled } from 'components/copilot/risk/riskMetrics.utils.ts';
import { RiskMetricsChartOptions } from 'components/copilot/risk/RiskMetricsChartOptions.tsx';
import { getRangeByLabel, type PredefinedRangeLabel } from 'components/predefinedDateRanges.ts';
import type { RiskChartWidgetState } from './RiskChartWidgetView.tsx';
import { WarmUpInput } from 'components/copilot/risk/WarmUpInput.tsx';
import WidgetSettings from 'components/management/customDashboard/WidgetSettings.tsx';
import gYupResolver from 'components/technical/form/gYupResolver.ts';
import type { MetricInput } from '../RiskMetricsChart.types.ts';
import { FormPredefinedDateRangeInput } from 'components/technical/form/FormPredefinedDateRangeInput.tsx';
import FormSwitch from 'components/technical/form/FormSwitch.tsx';
import { useSubAccountAssetFilters } from 'components/technical/UseSubAccountAssetFilters.tsx';
import { FormPortfolioFilters } from 'components/technical/form/FormPortfolioFilters.tsx';
import {
  type PortfolioFilters,
  portfolioFiltersSchema,
} from 'components/technical/PortfolioFilterDrawer/UsePortfolioFilters.tsx';

const schema = yup
  .object({
    title: yup.string().required(),
    metricInput: yup.object().required(),
    predefinedDateRange: yup.object().required(),
    warmUpPeriod: yup.string().required(),
    showContributionIfAvailable: yup.boolean().required(),
  })
  .concat(portfolioFiltersSchema);

interface FormState extends Partial<PortfolioFilters> {
  title: string;
  metricInput: MetricInput;
  predefinedDateRange: {
    label: PredefinedRangeLabel;
  };
  warmUpPeriod: string;
  showContributionIfAvailable: boolean;
}

const RiskChartWidgetSettings = (props: {
  id: string;
  onFinishedEditing: () => void;
  onRemovedWidget: () => Promise<void>;
}): ReactElement => {
  const initialState = useWidgetState<RiskChartWidgetState>(props.id);
  const subAccountAssetFilters = useSubAccountAssetFilters(initialState);
  const updateState = updateWidgetState<RiskChartWidgetState>();

  const methods = useForm<FormState>({
    resolver: gYupResolver(schema),
    defaultValues: {
      ...initialState,
      predefinedDateRange: getRangeByLabel(initialState.predefinedDateRange),
    },
  });

  const handleFormSubmit = async (input: FormState): Promise<void> => {
    await updateState({
      id: props.id,
      state: {
        ...input,
        predefinedDateRange: input.predefinedDateRange.label,
      },
      updateBackend: true,
    });
    props.onFinishedEditing();
  };

  const { data: dashboardInput } = useRiskDashboardInputSuspenseQuery({
    variables: {
      subAccountAssetFilters,
    },
  });

  const metricParameters = dashboardInput.portfolio.metricParameters;
  const labelToMetricParams = calculateLabelToMetricParams(metricParameters);

  const availableMetrics = Object.keys(labelToMetricParams);
  const metricInput = methods.watch('metricInput');
  const metricParamDescriptions = labelToMetricParams[metricInput.metric];
  const warmupEnabled = isWarmUpPeriodParamEnabled(metricInput, metricParamDescriptions);

  return (
    <WidgetSettings methods={methods} handleFormSubmit={handleFormSubmit} onRemovedWidget={props.onRemovedWidget}>
      <Controller
        name="metricInput"
        control={methods.control}
        render={({ field }) => (
          <RiskMetricsChartOptions
            metricInput={field.value}
            setMetricInput={field.onChange}
            metrics={availableMetrics}
            metricParameters={labelToMetricParams}
          />
        )}
      />
      <FormPredefinedDateRangeInput name={'predefinedDateRange'} label={'Date range'} />
      <FormPortfolioFilters />
      <Controller
        name="warmUpPeriod"
        control={methods.control}
        render={({ field }) => (
          <WarmUpInput
            disabled={methods.formState.isSubmitting || !warmupEnabled}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <FormSwitch name={'showContributionIfAvailable'} label={'Show Contribution'} />
    </WidgetSettings>
  );
};

const RiskChartWidgetSettingsContainer = (props: {
  id: string;
  onFinishedEditing: () => void;
  onRemovedWidget: () => Promise<void>;
}): ReactElement => {
  return (
    <Suspense fallback={<Loader />}>
      <RiskChartWidgetSettings {...props} />
    </Suspense>
  );
};

export default RiskChartWidgetSettingsContainer;
